import React, { Component } from 'react';
import moment from 'moment-timezone';
import { get, sumBy } from 'lodash';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom/cjs/react-router-dom';
import { deleteEvent } from 'redux/ducks/Event';
import { connect } from 'react-redux';
import { duplicatePost, clearV2Form, initForm } from 'redux/ducks/Post/actions';
import { getEventLocations, getFirstStartDate, getTimezone, isAmericanExpress } from 'helpers/post';
import {
  shiftStatistics,
  activeShifts,
  getAlerts,
  SHOW_TOOLTIP_COLUMNS,
  createOverflowChecker,
} from 'helpers/dashboard';
import { UncontrolledTooltip } from 'reactstrap';
import DeleteModal from 'components/V2/ConfirmationModal/DeleteModal';
import PaidJobModal from 'components/V2/PaidJobModal';
import {
  triggerBlockedFailedPaymentModal,
  triggerWaitUntilFailedPaymentProcessModal,
} from 'redux/ducks/Modals';
import { blockedByFailedPayment, pendingUnblockFailedPayment } from '@helpers/payment';

class UpcomingListItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOverflowing: {},
      isDeleteModalOpen: false,
    };
    this.columnRefs = {};
    SHOW_TOOLTIP_COLUMNS.upcoming.forEach(column => {
      this.columnRefs[column] = React.createRef();
    });
  }

  componentDidMount() {
    const checkOverflow = createOverflowChecker('upcoming', this.columnRefs, this.setState.bind(this));

    this.resizeObserver = new ResizeObserver(checkOverflow);
    SHOW_TOOLTIP_COLUMNS.upcoming.forEach(column => {
      this.resizeObserver.observe(this.columnRefs[column].current);
    });
  }

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  toggleDeleteModal = () => {
    this.setState({ isDeleteModalOpen: !this.state.isDeleteModalOpen });
  };

  checkForUnremovableShift = item =>
    Object.values(item.shifts.byId).some(shift =>
      Object.values(shift.jobs.byId).some(job => ['paid', 'checked_in'].includes(job.currentState))
    );

  hasFutureShifts = item =>
    Object.values(item.shifts.byId).some(shift => {
      const tz = getTimezone(shift.eventLocation);
      return moment.tz(shift.startTime, tz).isAfter(moment());
    });

  firstUnremovableShift = item =>
    Object.values(item.shifts.byId).find(shift =>
      Object.values(shift.jobs.byId).some(job => ['paid', 'checked_in'].includes(job.currentState))
    );

  onContinue = () => {
    const { item } = this.props;
    const shift = this.firstUnremovableShift(item);
    this.props.history.push(`/v2/upcoming-event/${item.id}/locations/${shift.eventLocation.id}`);
  };

  startDate = () => {
    const { item } = this.props;
    const shifts = Object.values(item.shifts.byId);

    if (item && shifts.length > 0) {
      const futureShifts = activeShifts(shifts);
      return getFirstStartDate(futureShifts).format('MM/DD/YY');
    }
  };

  boostedShift = shift => {
    return shift.boostedAt && moment(shift.boostedAt).isAfter(moment().subtract(48, 'hours'));
  };

  shiftPendingConfirmation = shift => {
    const { item } = this.props;
    const { capacityFilled, pendingConfirmation } = shiftStatistics(
      Object.values(get(shift, 'jobs.byId', {}))
    );
    if (moment(item.createdAt).isAfter(moment().subtract(48, 'hours'))) {
      return !!pendingConfirmation;
    } else {
      return pendingConfirmation + capacityFilled >= shift.shiftCapacity;
    }
  };

  shiftsFilled = shifts =>
    sumBy(shifts, shift => shiftStatistics(Object.values(shift.jobs.byId)).capacityFilled);

  eventCapacity = shifts => sumBy(shifts, 'shiftCapacity');

  shiftRequests = shifts =>
    sumBy(shifts, shift => shiftStatistics(Object.values(shift.jobs.byId)).shiftRequests);

  eventEndDate = () => {
    const { item } = this.props;
    const shifts = Object.values(item.shifts.byId);

    if (shifts.length === 0 || shifts.every(shift => !shift.isMultiDay)) return '';

    const [lastShift] = shifts.sort((a, b) => (moment(a.endTime).isBefore(moment(b.endTime)) ? 1 : -1));

    const tz = getTimezone(lastShift.eventLocation);

    if (moment(item.nextStartTime).format('MM/DD/YY') === moment.tz(lastShift.endTime, tz).format('MM/DD/YY'))
      return '';

    return ` - ${moment.tz(lastShift.endTime, tz).format('MM/DD/YY')}`;
  };

  handleAlert = () => {
    const { item } = this.props;
    const shifts = activeShifts(Object.values(item.shifts.byId));
    const shiftsFilled = this.shiftsFilled(shifts);
    const eventCapacity = this.eventCapacity(shifts);
    const shiftRequests = this.shiftRequests(shifts);

    if (shifts.length === 0) return 'ended';

    if (shiftsFilled === eventCapacity) return 'filled';

    if (shiftRequests > 0) return 'review';

    if (shifts.every(this.shiftPendingConfirmation)) return 'confirmation';

    if (moment(item.createdAt).isAfter(moment().subtract(48, 'hours'))) return 'new';

    if (shifts.every(this.boostedShift)) return 'boosted';

    return 'boost';
  };

  onEditEvent = () => {
    const { item } = this.props;
    this.props.initialize({
      ...item,
      shifts: Object.values(get(item, 'shifts.byId', {})),
    });
    this.props.history.push(`/v2/events/${item.id}/profile`);
  };

  onEventTitle = () => {
    const { item } = this.props;

    const locations = getEventLocations(item);
    if (locations.length === 1) {
      this.props.history.push(`/v2/upcoming-event/${item.id}/locations/${locations[0]?.id}`);
      return;
    }

    this.props.history.push(`/v2/upcoming-event/${item.id}/locations`);
  };

  onDeleteEvent = () => {
    this.props.onDelete(this.props.item);
  };

  onDuplicate = () => {
    const { item, user } = this.props;

    if (pendingUnblockFailedPayment(user)) {
      this.props.toggleWaitUntilFailedPaymentProcess();
      return;
    }

    if (blockedByFailedPayment(user)) {
      this.props.toggleBlockedForFailedPayments();
      return;
    }

    this.props.clearForm();
    this.props.duplicatePost(item);
    this.props.history.push(`/v2/create-event/profile`);
  };

  render() {
    const { item, columns } = this.props;
    const eventAlert = this.handleAlert();
    const { shiftsFilled, eventCapacity, shiftRequests } = item.statistics || {};
    const hasUnremovableShift = this.checkForUnremovableShift(item);
    const hasFutureShifts = this.hasFutureShifts(item);

    return (
      <div className="upcoming-list-item" key={item.id}>
        <div
          className={`${columns['startDate'].size}`}
          id={`startDate-${item.id}`}
          ref={this.columnRefs['startDate']}
        >
          {moment(item.nextStartTime).format('MM/DD/YY')}
          {this.eventEndDate()}
          {this.state.isOverflowing.startDate && (
            <UncontrolledTooltip target={`startDate-${item.id}`}>
              {moment(item.nextStartTime).format('MM/DD/YY')}
              {this.eventEndDate()}
            </UncontrolledTooltip>
          )}
        </div>
        <div
          className={`${columns['jobName'].size} job-name`}
          onClick={this.onEventTitle}
          id={`jobName-${item.id}`}
          ref={this.columnRefs['jobName']}
        >
          {item.title}
          {this.state.isOverflowing.jobName && (
            <UncontrolledTooltip target={`jobName-${item.id}`}>{item.title}</UncontrolledTooltip>
          )}
        </div>
        <div className={`${columns['filled'].size}`} id={`filled-${item.id}`} ref={this.columnRefs['filled']}>
          {shiftsFilled}/{eventCapacity}
          {this.state.isOverflowing.filled && (
            <UncontrolledTooltip target={`filled-${item.id}`}>
              {shiftsFilled}/{eventCapacity}
            </UncontrolledTooltip>
          )}
        </div>
        <div className={`${columns['alert'].size} ${eventAlert}`}>
          {eventAlert === 'review'
            ? `${shiftRequests} APPLICATION${shiftRequests > 1 ? 'S' : ''}`
            : get(
                getAlerts().find(alert => alert.key === eventAlert),
                'name'
              )}
        </div>
        <div
          className={classNames(`${columns['actions'].size} event-actions`)}
          style={{
            paddingRight: '10px',
          }}
        >
          <button className={classNames('plain-button edit-button')} onClick={() => this.onEditEvent(item)}>
            EDIT
          </button>
          <button className={classNames('plain-button delete-button')} onClick={this.onDuplicate}>
            DUPLICATE
          </button>
          {hasUnremovableShift ? (
            <PaidJobModal
              key={`paid-job-modal-${item.id}`}
              onContinue={this.onContinue}
              customBodyText={
                hasFutureShifts ? '' : 'Jobs cannot be cancelled once they are being worked on.'
              }
              hideContinueButton={!hasFutureShifts}
              item={item}
            >
              <span className="plain-button delete-button">CANCEL</span>
            </PaidJobModal>
          ) : (
            <button
              className={classNames('plain-button delete-button')}
              onClick={() => this.toggleDeleteModal()}
            >
              CANCEL
            </button>
          )}
        </div>
        <DeleteModal
          key={`delete-job-modal-${item.id}`}
          isOpen={!!this.state.isDeleteModalOpen}
          title="Cancel Job"
          body="Canceling this job will remove all shifts and athletic trainers who applied or confirmed to work."
          cancelText="Cancel Job"
          toggle={this.toggleDeleteModal}
          onDelete={this.props.onDelete}
          item={item}
          isEvent={true}
          user={this.props.user}
          activeShifts={activeShifts(Object.values(item.shifts.byId))}
          isAmex={isAmericanExpress(this.props.payment)}
          rateTypes={this.props.rateTypes}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.session.currentUser,
  payment: state.payment,
  rateTypes: state.enums.rateTypes,
});

const mapDispatchToProps = dispatch => ({
  onDelete: event => dispatch(deleteEvent(event)),
  duplicatePost: event => dispatch(duplicatePost(event)),
  clearForm: () => dispatch(clearV2Form()),
  initialize: originalEvent => dispatch(initForm(originalEvent)),
  toggleBlockedForFailedPayments: () => dispatch(triggerBlockedFailedPaymentModal()),
  toggleWaitUntilFailedPaymentProcess: () => dispatch(triggerWaitUntilFailedPaymentProcessModal()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UpcomingListItem));
